/**
 * @module @repo/global-login-sdk/ai-audit
 * Vollständigkeits-Report der KI-Kennzeichnung (LEG-005). Art. 50 KI-VO verlangt
 * ZWEI Ebenen (Konzept, Abschnitt 7): den **Rechtstext** (`ki.*`-Bausteine auf der
 * KI-/Datenschutzseite) UND die **Laufzeit-Kennzeichnung** an der Interaktion. Wer nur
 * eine Ebene hat, ist nicht konform — und genau diese Lücke ist ohne Report unsichtbar,
 * weil beide Ebenen an unterschiedlichen Stellen leben.
 *
 * Dieses Modul findet sie: welche App hat ein KI-Merkmal gesetzt, aber
 *
 * - (a) es greift kein zugehöriger KI-Rechtstext-Baustein (bzw. er greift, hat aber
 *   fehlende Pflicht-Platzhalter), oder
 * - (b) die Laufzeit-Kennzeichnung (`<AiDisclosureNotice>` / `<AiGeneratedBadge>` /
 *   `<AiDeepfakeNotice>`) ist nicht eingebunden?
 *
 * Bewusst React-frei und rein (netzwerkfrei, ohne DOM), damit dasselbe Ergebnis in
 * einem CI-Skript, im Admin-Board und in Tests entsteht. Die Regel-Auswertung kommt
 * über {@link resolveDocument} aus `@repo/shared-types` — es gibt keine zweite
 * Regel-Engine und keine zweite Merkmals-Quelle.
 */
import {
  resolveDocument,
  type LegalBlock,
  type LegalBooleanTrait,
  type LegalOverrideInput,
  type LegalPlaceholders,
  type LegalTraits,
  type ResolvedLegalBlock,
} from '@repo/shared-types';
import type { LegalContent } from './legal';

// ---------------------------------------------------------------------------
// Landkarte: Merkmal → Rechtstext-Baustein und Merkmal → Laufzeit-Kennzeichnung
// ---------------------------------------------------------------------------

/**
 * Merkmal → die `ki.*`-Bausteine, die es scharf schaltet (Konzept Abschnitt 7,
 * „KI-Kennzeichnung (7)"). `satisfies Partial<Record<LegalBooleanTrait, …>>` erzwingt
 * beim Typecheck, dass jeder Schlüssel ein echtes Merkmal der SSOT ist — die
 * Invariante „ein Merkmal, ein Name" gilt also auch hier.
 */
export const AI_TRAIT_BLOCK_KEYS = {
  aiSystems: [
    'ki.grundsatz-transparenz',
    'ki.rollen-und-geltung',
    'ki.datenverarbeitung',
    'ki.beschwerde-marktueberwachung',
  ],
  aiChatbot: ['ki.chatbot-offenlegung'],
  aiGeneratesContent: ['ki.generierte-inhalte'],
  aiDeepfakes: ['ki.deepfake-offenlegung'],
} as const satisfies Partial<Record<LegalBooleanTrait, readonly string[]>>;

/** Ein KI-Merkmal, das Rechtstext-Bausteine schaltet. */
export type AiLegalTrait = keyof typeof AI_TRAIT_BLOCK_KEYS;

/**
 * Merkmal → die zugehörige Laufzeit-Kennzeichnung. `aiSystems` fehlt bewusst: es ist
 * das Basis-Merkmal des Rechtstexts und hat keine eigene UI-Kennzeichnung.
 */
export const AI_RUNTIME_MARKINGS = {
  aiChatbot: 'chatbot',
  aiGeneratesContent: 'generatedContent',
  aiDeepfakes: 'deepfake',
} as const satisfies Partial<Record<LegalBooleanTrait, string>>;

/** Ein KI-Merkmal mit Laufzeit-Kennzeichnung (`aiChatbot`, `aiGeneratesContent`, `aiDeepfakes`). */
export type AiDisclosureTrait = keyof typeof AI_RUNTIME_MARKINGS;

/** Name einer Laufzeit-Kennzeichnung (`chatbot` | `generatedContent` | `deepfake`). */
export type AiDisclosureMarking = (typeof AI_RUNTIME_MARKINGS)[AiDisclosureTrait];

/** Alle Merkmale mit Rechtstext-Bezug — aus {@link AI_TRAIT_BLOCK_KEYS} abgeleitet. */
const AI_LEGAL_TRAITS = Object.keys(AI_TRAIT_BLOCK_KEYS) as readonly AiLegalTrait[];

/** Alle Merkmale mit Laufzeit-Kennzeichnung — aus {@link AI_RUNTIME_MARKINGS} abgeleitet. */
export const AI_DISCLOSURE_TRAITS = Object.keys(
  AI_RUNTIME_MARKINGS,
) as readonly AiDisclosureTrait[];

/** Liest ein KI-Merkmal typsicher aus den `traits` (nicht gesetzt = `false`). */
function isTraitSet(traits: LegalTraits, name: AiLegalTrait | AiDisclosureTrait): boolean {
  return traits[name] === true;
}

// ---------------------------------------------------------------------------
// Laufzeit-Beobachtung — was wurde im Browser wirklich gerendert?
// ---------------------------------------------------------------------------

/** Welche Laufzeit-Kennzeichnungen sind eingebunden/gerendert? */
export type AiDisclosureRuntimeUsage = Partial<Record<AiDisclosureMarking, boolean>>;

/** Modul-weite Beobachtung: jede Kennzeichnung, die im aktuellen Dokument gerendert hat. */
const renderedMarkings = new Set<AiDisclosureMarking>();

/**
 * Meldet, dass eine Laufzeit-Kennzeichnung tatsächlich gerendert wurde. Wird von den
 * SDK-Komponenten beim Mounten aufgerufen — NICHT, wenn das Merkmal sie ausschaltet,
 * denn dann wurde dem Nutzer auch nichts angezeigt.
 *
 * @param marking Die gerenderte Kennzeichnung.
 */
export function noteAiDisclosureRendered(marking: AiDisclosureMarking): void {
  renderedMarkings.add(marking);
}

/**
 * Liest die beobachteten Kennzeichnungen des aktuellen Dokuments — die Eingabe für
 * Prüfung (b) in einer laufenden App (Admin-Diagnose, E2E-Test).
 *
 * GRENZE: beobachtet wird nur, was in DIESEM Dokument gemountet hat. Eine fehlende
 * Kennzeichnung kann daher auch heißen „die betroffene Oberfläche wurde nicht besucht".
 * Für eine verlässliche CI-Prüfung übergibt man stattdessen den **deklarierten** Stand
 * der Integration an {@link auditAiDisclosure}.
 *
 * @returns Die beobachteten Kennzeichnungen (nur die tatsächlich gerenderten).
 */
export function readAiDisclosureRuntimeUsage(): AiDisclosureRuntimeUsage {
  const usage: AiDisclosureRuntimeUsage = {};
  for (const marking of renderedMarkings) usage[marking] = true;
  return usage;
}

/** Setzt die Beobachtung zurück (Tests, Routenwechsel in einer Diagnose-Oberfläche). */
export function resetAiDisclosureRuntimeUsage(): void {
  renderedMarkings.clear();
}

// ---------------------------------------------------------------------------
// Report
// ---------------------------------------------------------------------------

/** Art einer gefundenen Lücke. */
export type AiDisclosureGapKind =
  /** Merkmal gesetzt, aber der zugehörige Rechtstext-Baustein greift nicht (oder fehlt). */
  | 'legal-text-missing'
  /** Baustein greift, aber es fehlen Platzhalter-Werte → `[Angabe fehlt: …]` würde ausgeliefert. */
  | 'legal-text-incomplete'
  /** KI-Merkmal ohne `aiSystems` → die Basis-Bausteine der KI-Seite greifen nicht. */
  | 'base-trait-missing'
  /** Merkmal gesetzt, aber die Laufzeit-Kennzeichnung ist nicht eingebunden. */
  | 'runtime-marking-missing'
  /** Laufzeit-Kennzeichnung gerendert, obwohl das Merkmal nicht gesetzt ist (Widerspruch). */
  | 'runtime-marking-undeclared';

/** Eine gefundene Lücke. */
export type AiDisclosureGap = {
  readonly slug: string;
  readonly kind: AiDisclosureGapKind;
  /** Das auslösende KI-Merkmal. */
  readonly trait: AiLegalTrait | AiDisclosureTrait;
  /** Betroffene Baustein-Schlüssel (leer bei Laufzeit-Lücken). */
  readonly blockKeys: readonly string[];
  /** Betroffene Laufzeit-Kennzeichnung (`null` bei Rechtstext-Lücken). */
  readonly marking: AiDisclosureMarking | null;
  /** Klartext für Report/Admin-Badge. */
  readonly message: string;
};

/** Eingabe des Reports für EINE App. */
export type AiDisclosureAppInput = {
  /** App-Slug aus `identity.apps.slug` (nur für die Ausgabe). */
  readonly slug: string;
  /** Merkmale der App (`legal.app_profile.traits`) — dieselbe Quelle wie der Rechtstext. */
  readonly traits: LegalTraits;
  /** Der Baustein-Katalog (mindestens die `ki`-Kategorie). */
  readonly blocks: readonly LegalBlock[];
  readonly placeholders?: LegalPlaceholders;
  readonly overrides?: readonly LegalOverrideInput[];
  /**
   * Eingebundene/beobachtete Laufzeit-Kennzeichnungen. Fehlt der Wert, wird Prüfung (b)
   * NICHT durchgeführt und das als Warnung ausgegeben — nie stillschweigend als „ok".
   */
  readonly runtime?: AiDisclosureRuntimeUsage;
};

/** Report für eine App. */
export type AiDisclosureAppReport = {
  readonly slug: string;
  /** `true`, wenn keine Lücke gefunden wurde. */
  readonly ok: boolean;
  /** Die gesetzten KI-Merkmale (leer = die App erklärt keinen KI-Einsatz). */
  readonly activeTraits: readonly (AiLegalTrait | AiDisclosureTrait)[];
  readonly gaps: readonly AiDisclosureGap[];
  /** Regel-Warnungen der Auswertung plus Hinweise zur Prüftiefe. */
  readonly warnings: readonly string[];
};

/** Report über mehrere Apps. */
export type AiDisclosureReport = {
  readonly ok: boolean;
  readonly apps: readonly AiDisclosureAppReport[];
  /** Alle Lücken aller Apps, flach — für eine Tabelle im Admin-Board. */
  readonly gaps: readonly AiDisclosureGap[];
};

/** Baut die Report-Eingabe aus dem Ergebnis von `legalContentQueryOptions`. */
export function aiDisclosureAppInput(
  slug: string,
  content: LegalContent,
  runtime?: AiDisclosureRuntimeUsage,
): AiDisclosureAppInput {
  return {
    slug,
    traits: content.profile?.traits ?? {},
    blocks: content.blocks,
    placeholders: content.profile?.placeholders ?? {},
    overrides: content.overrides,
    runtime,
  };
}

/**
 * Prüft eine App auf Lücken zwischen KI-Merkmal, Rechtstext und Laufzeit-Kennzeichnung.
 *
 * Geprüft wird gegen das AUFGELÖSTE Dokument (`resolveDocument`, Kategorie `ki`) und
 * damit gegen genau das, was die App ausliefert — inklusive Overrides und inklusive der
 * Regel-Auswertung von `applies_when`. Ein Merkmal, dessen Baustein wegen einer
 * geänderten Regel nicht mehr greift, fällt so auf.
 *
 * @param app Siehe {@link AiDisclosureAppInput}.
 * @returns Der Report dieser App (`ok`, gefundene Lücken, Warnungen).
 */
export function auditAiDisclosure(app: AiDisclosureAppInput): AiDisclosureAppReport {
  const traits = app.traits;
  const legalDocument = resolveDocument({
    blocks: app.blocks,
    traits,
    placeholders: app.placeholders ?? {},
    overrides: app.overrides,
    category: 'ki',
  });
  const resolvedByKey = new Map(legalDocument.blocks.map((block) => [block.key, block] as const));
  const gaps: AiDisclosureGap[] = [];
  const warnings: string[] = [...legalDocument.warnings];
  const activeTraits: (AiLegalTrait | AiDisclosureTrait)[] = AI_LEGAL_TRAITS.filter((trait) =>
    isTraitSet(traits, trait),
  );

  const push = (gap: Omit<AiDisclosureGap, 'slug'>): void => {
    gaps.push({ slug: app.slug, ...gap });
  };

  // (a) Rechtstext-Ebene: greift je Merkmal der zugehörige Baustein — und ist er gefüllt?
  for (const trait of AI_LEGAL_TRAITS) {
    if (!isTraitSet(traits, trait)) continue;
    const expected: readonly string[] = AI_TRAIT_BLOCK_KEYS[trait];
    const missing = expected.filter((key) => !resolvedByKey.has(key));
    if (missing.length > 0) {
      push({
        kind: 'legal-text-missing',
        trait,
        blockKeys: missing,
        marking: null,
        message:
          `Merkmal '${trait}' ist gesetzt, aber der Rechtstext-Baustein ` +
          `${missing.map((key) => `'${key}'`).join(', ')} greift nicht — die KI-Seite ` +
          `liefert die zugehörige Pflichtangabe nicht aus`,
      });
    }
    const incomplete = expected
      .map((key) => resolvedByKey.get(key))
      .filter(
        (block): block is ResolvedLegalBlock => block !== undefined && block.missing.length > 0,
      );
    for (const block of incomplete) {
      push({
        kind: 'legal-text-incomplete',
        trait,
        blockKeys: [block.key],
        marking: null,
        message:
          `Baustein '${block.key}' greift für Merkmal '${trait}', es fehlen aber die ` +
          `Platzhalter-Werte ${block.missing.join(', ')} — ausgeliefert würde ` +
          `'[Angabe fehlt: …]'`,
      });
    }
  }

  // Basis-Invariante: die KI-Seite trägt ihre Rollen-/Datenschutz-Bausteine über
  // `aiSystems`. Ein spezifisches KI-Merkmal ohne `aiSystems` liefert deshalb einen
  // Chatbot-Hinweis ohne Rollenabgrenzung und ohne Verarbeitungsangaben.
  const specificWithoutBase = AI_DISCLOSURE_TRAITS.filter((trait) => isTraitSet(traits, trait));
  if (specificWithoutBase.length > 0 && !isTraitSet(traits, 'aiSystems')) {
    push({
      kind: 'base-trait-missing',
      trait: specificWithoutBase[0] ?? 'aiChatbot',
      blockKeys: [...AI_TRAIT_BLOCK_KEYS.aiSystems],
      marking: null,
      message:
        `${specificWithoutBase.join(', ')} ist gesetzt, 'aiSystems' aber nicht — die ` +
        `Basis-Bausteine der KI-Seite (Transparenz, Rollenabgrenzung Anbieter/Betreiber, ` +
        `Datenverarbeitung, Beschwerdewege) greifen dadurch nicht`,
    });
  }

  // (b) Laufzeit-Ebene: ist die Kennzeichnung dort, wo die Interaktion passiert?
  if (app.runtime === undefined) {
    warnings.push(
      'keine Laufzeit-Beobachtung übergeben — die Einbindung der Kennzeichnung ' +
        '(Ebene B) wurde NICHT geprüft',
    );
  } else {
    const runtime = app.runtime;
    for (const trait of AI_DISCLOSURE_TRAITS) {
      const marking = AI_RUNTIME_MARKINGS[trait];
      const declared = isTraitSet(traits, trait);
      const rendered = runtime[marking] === true;
      if (declared && !rendered) {
        push({
          kind: 'runtime-marking-missing',
          trait,
          blockKeys: [],
          marking,
          message:
            `Merkmal '${trait}' ist gesetzt, aber die Laufzeit-Kennzeichnung '${marking}' ` +
            `ist nicht eingebunden — ein Rechtstext allein erfüllt Art. 50 KI-VO nicht, ` +
            `die Offenlegung muss an der Interaktion stehen`,
        });
      }
      if (!declared && rendered) {
        push({
          kind: 'runtime-marking-undeclared',
          trait,
          blockKeys: [...AI_TRAIT_BLOCK_KEYS[trait]],
          marking,
          message:
            `Laufzeit-Kennzeichnung '${marking}' wird gerendert, das Merkmal '${trait}' ist ` +
            `im Profil aber nicht gesetzt — UI und Rechtstext widersprechen sich`,
        });
      }
    }
  }

  return { slug: app.slug, ok: gaps.length === 0, activeTraits, gaps, warnings };
}

/**
 * Prüft mehrere Apps in einem Durchgang — die Report-Sicht des Admin-Boards bzw. des
 * CI-Skripts („welche App hat eine KI-Lücke?").
 *
 * @param apps Die zu prüfenden Apps.
 * @returns Report mit Gesamt-`ok`, Pro-App-Ergebnis und der flachen Lückenliste.
 */
export function auditAiDisclosureApps(apps: readonly AiDisclosureAppInput[]): AiDisclosureReport {
  const reports = apps.map((app) => auditAiDisclosure(app));
  return {
    ok: reports.every((report) => report.ok),
    apps: reports,
    gaps: reports.flatMap((report) => report.gaps),
  };
}

/** Präfix einer App-Zeile im Textreport (feste Breite, damit die Liste scanbar bleibt). */
const REPORT_MARK = { ok: '[ok] ', gap: '[!]  ' } as const;

/**
 * Formatiert den Report als Klartext für ein Skript oder eine Konsolen-Ausgabe im
 * Admin-Board. Bewusst ohne Farben/Emojis, damit die Ausgabe in CI-Logs lesbar bleibt.
 *
 * @param report Ergebnis von {@link auditAiDisclosureApps}.
 * @returns Der mehrzeilige Report-Text.
 */
export function formatAiDisclosureReport(report: AiDisclosureReport): string {
  const withGaps = report.apps.filter((app) => !app.ok);
  const lines: string[] = [
    'KI-Kennzeichnung — Vollständigkeits-Report (Art. 50 KI-VO, Ebene A + B)',
    `${report.apps.length} App(s) geprüft · ${withGaps.length} mit Lücken · ` +
      `${report.gaps.length} Lücke(n) insgesamt`,
    '',
  ];
  for (const app of report.apps) {
    const traits = app.activeTraits.length > 0 ? app.activeTraits.join(', ') : 'keine';
    lines.push(`${app.ok ? REPORT_MARK.ok : REPORT_MARK.gap}${app.slug} — KI-Merkmale: ${traits}`);
    for (const gap of app.gaps) lines.push(`       - ${gap.kind}: ${gap.message}`);
    for (const warning of app.warnings) lines.push(`       ! ${warning}`);
  }
  return lines.join('\n');
}
